// Modules
import { Link } from 'react-router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { Badge, yesIcon } from 'components/widgets/badges/badges';
import { Page, PageHeader, PageContent, ActionSection } from 'components/page';
import { SortableTH } from 'components/content-containers/tables';
import { Table } from 'components/content-containers/tables/tables';
import { alerts } from 'modules/alerts/actions';
import { didQueryUpdate } from 'utils/filter-utils';
import Company from 'services/data/company';
import Filters from 'modules/filters/filters';
import FlipMove from 'components/widgets/animation/flip-move';
import Tooltip from 'components/widgets/overlays/tooltip';

import * as actions from './store/actions';


// Component
class Xboard extends React.Component {
  static propTypes = {
    companies: PropTypes.array,
    fetching: PropTypes.bool,
    query: PropTypes.object,
    currentUser: PropTypes.object.isRequired
  };

  constructor() {
    super();

    this.search = this.search.bind(this);

    this.filterConfig = {
      basic: ['company'],
      advanced: [
        ['company', 'team', 'status']
      ],
      defaults: {}
    };
  }

  componentWillReceiveProps(nextProps) {
    if (didQueryUpdate(nextProps, this.props)) this.search(nextProps.query);
  }

  componentWillUnmount() {
    actions.set({ companies: [], count: null });
  }

  search(query) {
    actions.set({ fetching: true });

    const q = _.defaults({
      load: { forms: { title: { in: ['Onboarding', 'Offboarding'] } } },
      where: { 'forms.title': { in: ['Onboarding', 'Offboarding'] }, id: _.get(query, 'where.company'), team: _.get(query, 'where.team') }
    }, Company.formatQuery(query));

    delete q.sort;
    delete q.limit;
    delete q.offset;

    return Company.find(q)
      .then(companies => {
        actions.set({ fetching: false, companies });
      })
      .catch(alerts.catchError);
  }

  render() {
    const { companies = [], fetching, query } = this.props;
    const month = moment().subtract(30, 'days');
    const year = moment().subtract(1, 'year');
    const sort = query.sort || { field: 'name', direction: 'asc' };
    const order = _.toLower(sort.direction);

    const mappedCompanies = _.map(companies, company => {
      const onForm = _.find(company.forms, { title: 'Onboarding' }) || {};
      const offForm = _.find(company.forms, { title: 'Offboarding' }) || {};
      const monthlyOnResponses = _.filter(onForm.responses, response => moment(response.timestamp).isAfter(month));
      const monthlyOffResponses = _.filter(offForm.responses, response => moment(response.timestamp).isAfter(month));
      const yearlyOnResponses = _.filter(onForm.responses, response => moment(response.timestamp).isAfter(year));
      const yearlyOffResponses = _.filter(offForm.responses, response => moment(response.timestamp).isAfter(year));
      const [on30, off30, on365, off365] = [monthlyOnResponses.length, monthlyOffResponses.length, yearlyOnResponses.length, yearlyOffResponses.length];
      const net30 = on30 - off30;
      const net365 = on365 - off365;

      return _.assign({ onForm, offForm, on30, off30, on365, off365, net30, net365 }, company);
    });

    const sortedCompanies = sort.field === 'name' ?
      _.orderBy(mappedCompanies, [sort.field], [order]) :
      _.orderBy(mappedCompanies, [
        company => company[sort.field] === 0,
        company => company[sort.field] < 0,
        company => Math.abs(company[sort.field])
      ], ['asc', 'asc', order]);

    return (
      <Page>
        <PageHeader title="xBoard">
          <ActionSection>
            <Filters config={this.filterConfig} />
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching}
          emptyText="No companies, adjust your filters!"
          emptyTextIcon="svg/company.svg"
          showEmptyText={!fetching && _.isEmpty(companies)}
          className="companies"
        >
          <div className="xboard-table">
            <Table className="collapse-table">
              <thead>
                <tr>
                  <th></th>
                  <th colSpan="3" className="center-align">30 Days</th>
                  <th colSpan="3" className="center-align">1 Year</th>
                  <th></th>
                </tr>
                <tr>
                  <SortableTH sortField="name">Company</SortableTH>
                  <SortableTH sortField="on30" className="center-align">On</SortableTH>
                  <SortableTH sortField="off30" className="center-align">Off</SortableTH>
                  <SortableTH sortField="net30" className="center-align">Net</SortableTH>
                  <SortableTH sortField="on365" className="center-align">On</SortableTH>
                  <SortableTH sortField="off365" className="center-align">Off</SortableTH>
                  <SortableTH sortField="net365" className="center-align">Net</SortableTH>
                  <th></th>
                </tr>
              </thead>
              <FlipMove typeName="tbody" enterAnimation="fade" leaveAnimation="fade">
                {_.map(sortedCompanies, company => {
                  const { onForm, offForm, on30, off30, on365, off365 } = company;

                  return (
                    <tr key={company.id}>
                      <td className="expand-cell">
                        <Link to={`/companies/${company.id}/forms`}>{company.name}</Link>
                      </td>
                      <td className="center-align">
                        <Link className="unadorned" to={`/companies/${company.id}/forms/${onForm.id}/responses`}>
                          <Badge className="light" number={on30}>{on30}</Badge>
                        </Link>
                      </td>
                      <td className="center-align">
                        <Link className="unadorned" to={`/companies/${company.id}/forms/${offForm.id}/responses`}>
                          <Badge className="light" number={off30 * -1}>{off30}</Badge>
                        </Link>
                      </td>
                      <td className="center-align"><Badge number={on30 - off30}>{on30 - off30}</Badge></td>
                      <td className="center-align">
                        <Link className="unadorned" to={`/companies/${company.id}/forms/${onForm.id}/responses`}>
                          <Badge className="light" number={on365}>{on365}</Badge>
                        </Link>
                      </td>
                      <td className="center-align">
                        <Link className="unadorned" to={`/companies/${company.id}/forms/${offForm.id}/responses`}>
                          <Badge className="light" number={off365 * -1}>{off365}</Badge>
                        </Link>
                      </td>
                      <td className="center-align"><Badge number={on365 - off365}>{on365 - off365}</Badge></td>
                      <td className="center-align">
                        { company.hasUserEscalator && <Tooltip title="Company has a user elevator">{yesIcon}</Tooltip> }
                      </td>
                    </tr>
                  );
                })}
              </FlipMove>
            </Table>
          </div>
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  companies: state.companies.companies,
  fetching: state.companies.fetching,
  query: state.location.query,
  currentUser: state.auth.currentUser
}))(Xboard);



// WEBPACK FOOTER //
// ./app/routes/company/view-xboard.js